import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  Clock,
  LogOut,
  Mail,
  MessageSquare,
  Phone,
  Send,
  Star,
  Trash2,
  Users,
  RefreshCw,
} from "lucide-react";
import { Button } from "../ui/button";
import { adminFetch, clearToken, getToken } from "../../lib/adminApi";
import { toast } from "sonner";

const TabBtn = ({ active, onClick, icon: Icon, label, count, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm transition-all duration-300 ${
      active
        ? "bg-gradient-to-r from-pink-400 to-purple-400 text-white shadow-md"
        : "bg-white text-purple-700 border-2 border-purple-200 hover:border-purple-400"
    }`}
  >
    <Icon className="h-4 w-4" />
    {label}
    {typeof count === "number" && (
      <span
        className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
          active ? "bg-white/30" : "bg-purple-100"
        }`}
      >
        {count}
      </span>
    )}
  </button>
);

const StatusPill = ({ status }) => {
  const map = {
    pending: { bg: "bg-yellow-100", txt: "text-yellow-800", label: "Pending" },
    completed: { bg: "bg-green-100", txt: "text-green-800", label: "Completed" },
  };
  const s = map[status] || map.pending;
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${s.bg} ${s.txt}`}>
      {s.label}
    </span>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("contacts");
  const [contacts, setContacts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [adminEmail, setAdminEmail] = useState("");

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login", { replace: true });
      return;
    }
    loadAll();
    // eslint-disable-next-line
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [meR, cR, tR] = await Promise.all([
        adminFetch("/api/admin/me"),
        adminFetch("/api/admin/contacts"),
        adminFetch("/api/admin/testimonials"),
      ]);
      setAdminEmail((await meR.json()).email);
      setContacts(await cR.json());
      setTestimonials(await tR.json());
    } catch (e) {
      if (e.status === 401) {
        navigate("/admin/login", { replace: true });
        return;
      }
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleMarkCompleted = async (c) => {
    if (!window.confirm(`Mark "${c.name}" as completed and send the review invite email?`)) return;
    setBusyId(c.id);
    try {
      const r = await adminFetch(`/api/admin/contacts/${c.id}/complete`, { method: "PATCH" });
      const data = await r.json();
      toast.success("Marked as completed", {
        description: data.invite_sent ? "Review invite email sent." : "Marked but email failed to send.",
      });
      await loadAll();
    } catch (e) {
      toast.error("Could not mark as completed");
    } finally {
      setBusyId(null);
    }
  };

  const handleDeleteTestimonial = async (t) => {
    if (!window.confirm(`Delete the review from ${t.name}? This cannot be undone.`)) return;
    setBusyId(t.id);
    try {
      await adminFetch(`/api/admin/testimonials/${t.id}`, { method: "DELETE" });
      toast.success("Review deleted");
      setTestimonials((prev) => prev.filter((x) => x.id !== t.id));
    } catch (e) {
      toast.error("Could not delete review");
    } finally {
      setBusyId(null);
    }
  };

  const handleLogout = async () => {
    try { await adminFetch("/api/admin/logout", { method: "POST" }); } catch {}
    clearToken();
    navigate("/admin/login", { replace: true });
  };

  const fmtDate = (d) => {
    try {
      const dt = new Date(d);
      return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch { return ""; }
  };

  return (
    <div
      data-testid="admin-dashboard"
      className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50"
    >
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b-4 border-purple-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-purple-700">Hola Spanglish · Admin</h1>
            <p className="text-xs text-purple-500">{adminEmail}</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={loadAll}
              data-testid="admin-refresh"
              variant="outline"
              className="border-2 border-purple-300 text-purple-700 hover:bg-purple-50 rounded-full"
            >
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </Button>
            <Button
              onClick={handleLogout}
              data-testid="admin-logout"
              variant="outline"
              className="border-2 border-pink-300 text-pink-700 hover:bg-pink-50 rounded-full"
            >
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex flex-wrap gap-3 mb-6">
          <TabBtn
            active={tab === "contacts"}
            onClick={() => setTab("contacts")}
            icon={Users}
            label="Contacts"
            count={contacts.length}
            testId="tab-contacts"
          />
          <TabBtn
            active={tab === "testimonials"}
            onClick={() => setTab("testimonials")}
            icon={Star}
            label="Reviews"
            count={testimonials.length}
            testId="tab-testimonials"
          />
        </div>

        {loading ? (
          <div className="text-center text-purple-500 py-20">Loading…</div>
        ) : tab === "contacts" ? (
          <div data-testid="contacts-table" className="bg-white rounded-3xl border-3 border-purple-200 shadow-md overflow-hidden">
            {contacts.length === 0 ? (
              <div className="p-10 text-center text-purple-500">No contacts yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gradient-to-r from-pink-50 to-purple-50 text-purple-700">
                    <tr>
                      <th className="text-left px-5 py-3 font-bold">Name</th>
                      <th className="text-left px-5 py-3 font-bold">Email</th>
                      <th className="text-left px-5 py-3 font-bold">Phone</th>
                      <th className="text-left px-5 py-3 font-bold">Level</th>
                      <th className="text-left px-5 py-3 font-bold">Date</th>
                      <th className="text-left px-5 py-3 font-bold">Status</th>
                      <th className="text-right px-5 py-3 font-bold">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map((c) => (
                      <tr
                        key={c.id}
                        data-testid={`contact-row-${c.id}`}
                        className="border-t-2 border-purple-100 hover:bg-purple-50/40 transition-colors"
                      >
                        <td className="px-5 py-3 font-semibold text-purple-800">{c.name}</td>
                        <td className="px-5 py-3 text-gray-700">
                          <a href={`mailto:${c.email}`} className="hover:text-purple-700">{c.email}</a>
                        </td>
                        <td className="px-5 py-3 text-gray-700">{c.phone || "—"}</td>
                        <td className="px-5 py-3 text-gray-700 capitalize">{c.level || "—"}</td>
                        <td className="px-5 py-3 text-gray-500">{fmtDate(c.created_at)}</td>
                        <td className="px-5 py-3"><StatusPill status={c.status} /></td>
                        <td className="px-5 py-3 text-right">
                          {c.status === "completed" ? (
                            <span className="inline-flex items-center gap-1 text-green-700 text-xs font-bold">
                              <CheckCircle2 className="h-4 w-4" /> Invite sent
                            </span>
                          ) : (
                            <Button
                              data-testid={`mark-completed-${c.id}`}
                              onClick={() => handleMarkCompleted(c)}
                              disabled={busyId === c.id}
                              size="sm"
                              className="bg-gradient-to-r from-green-300 to-green-400 hover:from-green-400 hover:to-green-500 text-green-900 font-bold rounded-full text-xs"
                            >
                              <Send className="h-3.5 w-3.5 mr-1" />
                              {busyId === c.id ? "Sending…" : "Mark as completed"}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          <div data-testid="reviews-grid" className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {testimonials.length === 0 ? (
              <div className="col-span-full p-10 text-center text-purple-500 bg-white rounded-3xl border-3 border-purple-200">
                No reviews yet.
              </div>
            ) : testimonials.map((t) => (
              <div
                key={t.id}
                data-testid={`review-row-${t.id}`}
                className="bg-white rounded-3xl border-3 border-purple-200 p-5 shadow-sm hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center gap-1 mb-2">
                  {[1,2,3,4,5].map((i) => (
                    <Star
                      key={i}
                      className={`h-4 w-4 ${i <= t.rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}`}
                    />
                  ))}
                </div>
                <p className="text-sm text-gray-700 leading-relaxed mb-3 line-clamp-5">
                  "{t.comment}"
                </p>
                <div className="border-t-2 border-purple-100 pt-3 mb-3">
                  <p className="font-bold text-purple-700 text-sm">{t.name}</p>
                  <p className="text-xs text-gray-500">
                    {[t.program, t.location].filter(Boolean).join(" · ") || "—"}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {t.email || "no email"} · {fmtDate(t.created_at)}
                  </p>
                </div>
                <Button
                  data-testid={`delete-review-${t.id}`}
                  onClick={() => handleDeleteTestimonial(t)}
                  disabled={busyId === t.id}
                  variant="outline"
                  size="sm"
                  className="w-full border-2 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 rounded-full text-xs font-bold"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" />
                  {busyId === t.id ? "Deleting…" : "Delete review"}
                </Button>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
